import { EMPTY_REVENUE_ANALYTICS_COLUMN_FILTERS, REVENUE_ANALYTICS_FILTER_COLUMNS } from './state';
import type { RevenueAnalyticsColumnFilters, RevenueAnalyticsFilterColumn } from './state';

/** Query-string key carrying the originating (member) firm; absent for every firm. */
export const REVENUE_ANALYTICS_MEMBER_FIRM_PARAM = 'memberFirmId';
/** Query-string key carrying the committed symbol search; absent when no search is applied. */
export const REVENUE_ANALYTICS_SYMBOL_PARAM = 'symbol';
/** Query-string key per numeric column filter, carrying the committed filter text verbatim. */
export const REVENUE_ANALYTICS_COLUMN_FILTER_PARAMS: Readonly<Record<RevenueAnalyticsFilterColumn, string>> = {
    qty: 'qty',
    avgPrice: 'avgPrice',
    totalCost: 'totalCost',
    curPrice: 'curPrice',
};

/** The filters a Revenue Analytics URL carries: everything that narrows the rows except the provider picker. */
export interface RevenueAnalyticsUrlFilters {
    readonly memberFirmId: string | undefined;
    /** Committed symbol term; `''` when no search is applied. */
    readonly symbol: string;
    readonly columnFilters: RevenueAnalyticsColumnFilters;
}

const trimmed = (value: string | null): string => value?.trim() ?? '';

/** The filters a location `search` string selects; blanks read as "no filter". */
export function parseRevenueAnalyticsFilters(search: string): RevenueAnalyticsUrlFilters {
    const params = new URLSearchParams(search);
    const memberFirmId = trimmed(params.get(REVENUE_ANALYTICS_MEMBER_FIRM_PARAM));
    const columnFilters = { ...EMPTY_REVENUE_ANALYTICS_COLUMN_FILTERS };
    for (const column of REVENUE_ANALYTICS_FILTER_COLUMNS) {
        columnFilters[column] = trimmed(params.get(REVENUE_ANALYTICS_COLUMN_FILTER_PARAMS[column]));
    }
    return {
        memberFirmId: memberFirmId === '' ? undefined : memberFirmId,
        symbol: trimmed(params.get(REVENUE_ANALYTICS_SYMBOL_PARAM)),
        columnFilters,
    };
}

/**
 * `search` with the filter params replaced by `filters`, other params
 * untouched. An absent filter is carried by absence so plain links stay
 * clean; the parser reads blanks and absence the same way, so the two
 * functions are inverse. Empty when nothing remains, `?`-prefixed
 * otherwise, so it can be handed straight to a navigation target.
 */
export function withRevenueAnalyticsFilters(search: string, filters: RevenueAnalyticsUrlFilters): string {
    const params = new URLSearchParams(search);
    const set = (key: string, value: string | undefined) => {
        params.delete(key);
        const normalized = value?.trim() ?? '';
        if (normalized !== '') params.set(key, normalized);
    };
    set(REVENUE_ANALYTICS_MEMBER_FIRM_PARAM, filters.memberFirmId);
    set(REVENUE_ANALYTICS_SYMBOL_PARAM, filters.symbol);
    for (const column of REVENUE_ANALYTICS_FILTER_COLUMNS) {
        set(REVENUE_ANALYTICS_COLUMN_FILTER_PARAMS[column], filters.columnFilters[column]);
    }
    return params.size === 0 ? '' : `?${params.toString()}`;
}
